import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import React from "react";
import { useSelector } from "react-redux";
import { isFeatureEnabled } from "src/utilities";

import { CustomerGroupListItem } from "../../../lib/models/CustomerGroupListItem";
import { StoreState } from "../../../store";
import {
  AgeGroupLabel,
  AreaTypeLabel,
  ComplaintTypeLabel,
  CustomerTypeLabel,
  ElectricityFilter,
  GasFilter,
  GlassFiberFilter,
  HeatingFilter,
  SupplyTypeLabel,
  TernaryFilterOption,
} from "../../../types/index";
interface Props {
  item: CustomerGroupListItem;
}

const useStyles = makeStyles(() => ({
  content: {
    margin: 10,
  },
}));

const FilterSettingsPopover: React.FC<Props> = ({ item }: Props) => {
  const classes = useStyles();

  const housingSituations = useSelector(
    (state: StoreState) => state.housingSituations
  );

  // eslint-disable-next-line @typescript-eslint/ban-types
  function checkNullValues(obj: Object) {
    for (const member in obj) {
      if (obj[member] === null) {
        return true;
      }
    }
    return false;
  }

  const renderFilterSettings = (item: CustomerGroupListItem) => {
    if (checkNullValues(item.filterSettings)) {
      return (
        <>
          <Typography className={classes.content}>
            Diese Kundengruppe wurde ohne Filter erstellt.
          </Typography>
        </>
      );
    }

    const getTernaryFilterOptionChosen = (filter: TernaryFilterOption) => {
      if (filter === TernaryFilterOption.INCLUDE) {
        return "ja";
      } else if (filter === TernaryFilterOption.REJECT) {
        return "nein";
      } else {
        return "";
      }
    };

    const getContractExistingFilterValue = (
      filter: ElectricityFilter | GasFilter | HeatingFilter | GlassFiberFilter
    ) => {
      return getTernaryFilterOptionChosen(filter.contractExistingFilter);
    };

    const getHeatingFilterInfo = () => {
      const filter = item.filterSettings.heatingFilter;
      return (
        <>
          <strong>Fernwärme:</strong>
          <ul
            style={{
              listStyleType: "none",
              marginTop: 0,
              marginBottom: 0,
            }}
          >
            <li>
              <strong>Vertrag vorhanden:</strong>{" "}
              {getContractExistingFilterValue(filter)}
            </li>
            <li>
              <strong>Tarife:</strong>{" "}
              {filter.tariffs.map((tariff) => tariff.productName).join(",")}
            </li>
          </ul>
        </>
      );
    };

    const getGlassFiberFilterInfo = () => {
      const filter = item.filterSettings.glassFiberFilter;
      return (
        <>
          <strong>Glasfaser:</strong>
          <ul
            style={{
              listStyleType: "none",
              marginTop: 0,
              marginBottom: 0,
            }}
          >
            <li>
              <strong>Vertrag vorhanden:</strong>{" "}
              {getContractExistingFilterValue(filter)}
            </li>
            <li>
              <strong>Produktfamilien:</strong>{" "}
              {filter.productFamilies.join(",")}
            </li>
            <li>
              <strong>Tarife:</strong>{" "}
              {filter.tariffs.map((tariff) => tariff.productName).join(",")}
            </li>
          </ul>
        </>
      );
    };

    const getElectricityOrGasFilterInfo = (type: string) => {
      const filter =
        type === "electricity"
          ? item.filterSettings.electricityFilter
          : item.filterSettings.gasFilter;
      return (
        <>
          <strong>{type === "electricity" ? "Strom:" : "Gas:"}</strong>{" "}
          <ul
            style={{
              listStyleType: "none",
              marginTop: 0,
              marginBottom: 0,
            }}
          >
            <li>
              <strong>Vertrag vorhanden:</strong>{" "}
              {getContractExistingFilterValue(filter)}
            </li>
            <li>
              <strong>Kundenregion:</strong>{" "}
              {filter.areaTypeFilter && AreaTypeLabel[filter.areaTypeFilter]}
            </li>
            <li>
              <strong>Versorgungsart:</strong>{" "}
              {filter.supplyTypeFilter &&
                SupplyTypeLabel[filter.supplyTypeFilter]}
            </li>
            <li>
              <strong>Produktfamilien:</strong>{" "}
              {filter.productFamilies.join(",")}
            </li>
            <li>
              <strong>Tarife:</strong>{" "}
              {filter.tariffs.map((tariff) => tariff.productName).join(",")}
            </li>
          </ul>
        </>
      );
    };

    const customerLabels =
      item.filterSettings.customerType
        .map((item) => CustomerTypeLabel[item])
        .join(", ") || "";

    let activeChurned = "";
    activeChurned = item.filterSettings.activeChurnedType
      ? "Aktiv"
      : "Nicht Aktiv";

    const getBuildingTypeNames = (ids: number[]) =>
      ids
        .map(
          (id) =>
            housingSituations.buildingTypes.find(
              (buildingType) => buildingType.id === id
            )?.name
        )
        .join(", ");

    const getBuildingAgeNames = (ids: number[]) =>
      ids
        .map(
          (id) =>
            housingSituations.buildingAges.find(
              (buildingAge) => buildingAge.id === id
            )?.name
        )
        .join(", ");
    return (
      <>
        <Typography className={classes && classes.content}>
          {item.filterSettings.filterGroupsEnabled.customerValueAnalysis && (
            <>
              <strong>CLV:</strong>{" "}
              {item.filterSettings.minClv + " - " + item.filterSettings.maxClv}
              <br />
              <strong>Churn Score:</strong>{" "}
              {item.filterSettings.minChurn +
                " - " +
                item.filterSettings.maxChurn}{" "}
              <br />
            </>
          )}
          <strong>Kundentyp:</strong> {customerLabels} <br />
          {item.filterSettings.filterGroupsEnabled.electricity &&
            getElectricityOrGasFilterInfo("electricity")}
          {item.filterSettings.filterGroupsEnabled.gas &&
            getElectricityOrGasFilterInfo("gas")}
          {item.filterSettings.filterGroupsEnabled.heating &&
            getHeatingFilterInfo()}
          {item.filterSettings.filterGroupsEnabled.glassFiber &&
            getGlassFiberFilterInfo()}
          {item.filterSettings.filterGroupsEnabled.nextBestActivity && (
            <>
              <strong>Beratungsfokus / NBA:</strong>{" "}
              {item.filterSettings.nextBestActivityFilter.join(", ")} <br />
            </>
          )}
          {item.filterSettings.filterGroupsEnabled.customerInformation && (
            <>
              <strong>Altersgruppe:</strong>{" "}
              {AgeGroupLabel[item.filterSettings.ageGroupType]} <br />
              <strong>Anrede:</strong> {item.filterSettings.salutation} <br />
              <strong>Beschwerdedatum: </strong>
              {ComplaintTypeLabel[item.filterSettings.complaintType]} <br />
              <strong>Kunde seit (Jahren):</strong>{" "}
              {item.filterSettings.customerDuration[0] +
                " - " +
                item.filterSettings.customerDuration[1]}
              <br />
              <strong>Postleitzahl: </strong>
              {item.filterSettings.customerAddressPostalCodes
                .map((item) => item.value)
                .join(", ")}{" "}
              <br />
              <strong>Aktiv aber gekündigt: </strong>
              {activeChurned}
              <br />
            </>
          )}
          {!!item.filterSettings.polygonMapSelection.length && (
            <>
              <strong>Kartenauswahl: </strong>
              `${
                item.filterSettings.polygonMapSelection.length
              } Bereich(e)` <br />
            </>
          )}
          {item.filterSettings.filterGroupsEnabled.customerContactChannels && (
            <>
              <strong>Opt-In Telefon: </strong>
              {getTernaryFilterOptionChosen(item.filterSettings.optIn.phone)}
              <br />
              <strong>Opt-In E-Mail: </strong>
              {getTernaryFilterOptionChosen(item.filterSettings.optIn.email)}
              <br />
              <strong>Opt-In Brief: </strong>
              {getTernaryFilterOptionChosen(item.filterSettings.optIn.mail)}
              <br />
              <strong>Telefonnummer vorhanden: </strong>
              {getTernaryFilterOptionChosen(item.filterSettings.phone)}
              <br />
              <strong>E-Mail Adresse vorhanden: </strong>
              {getTernaryFilterOptionChosen(item.filterSettings.email)}
              <br />
            </>
          )}
          {isFeatureEnabled(process.env.REACT_APP_FEATURE_HOUSING_SITUATION) &&
            item.filterSettings.filterGroupsEnabled.housingSituations && (
              <>
                <strong>Gebäudetyp: </strong>
                {getBuildingTypeNames(
                  item.filterSettings.housingSituationsFilter.buildingTypes
                )}
                <br />
                <strong>Gebäudealter: </strong>
                {getBuildingAgeNames(
                  item.filterSettings.housingSituationsFilter.buildingAges
                )}
                <br />
              </>
            )}
        </Typography>
      </>
    );
  };
  return renderFilterSettings(item);
};

export default FilterSettingsPopover;



// WEBPACK FOOTER //
// ./src/components/CustomerGroups/CustomerGroup/FilterSettingsPopover.tsx